import React, {useContext, useState} from 'react'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup, Stack,
    TextField,
    Theme,
    Typography,
} from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import CheckIcon from '@mui/icons-material/Check'
import {AppContext} from '../../contexts/AppContextProvider'
import ControlContainer from '../ControlContainer'
import {CancelButton, PreviousButton} from '../StepperButtons'
import {GeneralComplaintState, MaterialResponse, ProductTypeOptions, ProductIdOptions, YesNoOptions,} from '../../contexts/types'
import {useForm} from '../../hooks/useForm'
import {resetClinicalCondition} from '../../contexts/ClinicalConditionReducer'
import IRSAlert from '../IRSAlert'
import axios from 'axios'
import http from '../../http'
import {OrgCustomizationContext} from "../../themes/OrgCustomizationProvider";
import {resetTreatmentDates, setTreatmentDates} from "../../contexts/TreatmentDatesReducer";
import {resetComplaintType} from "../../contexts/ComplaintTypeReducer";
import {setGeneralComplaint} from "../../contexts/GeneralComplaintReducer";
import UseTheHackiestPageNavigation from "../../hooks/useTheHackiestPageNavigation";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        table: {
            border: '1px solid #9D9D9D',
            backgroundColor: theme.palette.grey[200],
            padding: '10px 20px',
            color: '#656565',
        },
        buttons: {
            '& > *': {
                margin: theme.spacing(1),
            },
        },
        title: {
            display: 'flex',
            alignItems: 'center',
        },
        tableHeading: {
            fontWeight: 'bold',
        },
    })
)

const ProductType: React.FC = () => {
    const classes = useStyles()
    const {navigateForward} = UseTheHackiestPageNavigation()
    const {t, orgIdentifier, ThirdPartyError,language} = useContext(OrgCustomizationContext)
    const {
        complaint,
        dispatch,
        countryCode,
        customerNumber,
        postNotification,
        clearNotification
    } = useContext(AppContext)
    const [isValidatingBatchNum, setIsValidatingBatchNum] =
        useState<boolean>(false)
    const [isBatchNumValid, setIsBatchNumValid] = useState<boolean>(false) // Start as invalid until validated
    const [showBatchNumCheck, setShowBatchNumCheck] = useState<boolean>(false)
    const [wasValidated, setWasValidated] = useState<boolean>(false) // Track if field has been validated
    const selectedProduct = complaint.generalComplaint.selectedProduct
    const required = {value: true, message: t('this_field_is_required')}
    const {
        data,
        errors,
        handleChangeEvent,
        validateFields,
        enableOnChangeValidation,
        setFormData,
    } = useForm<GeneralComplaintState>({
        initialValues: {
            ...complaint.generalComplaint,
            productType: selectedProduct?.productId === 'XUN001' ? 'IMPLANT' : complaint.generalComplaint.selectedProduct?.productType
        },
        validations: {
            isBatchOrLotNumberKnown: {
                custom: {
                    isValid: (value) => {
                        // Only required for Nobel organization
                        if (orgIdentifier === "Nobel") {
                            return !!value;
                        }
                        return true; // Always valid for IMDI (not shown in UI)
                    },
                    message: t('this_field_is_required'),
                },
            },
            wasProductPlaced: {required},
            productType: {required},
            wasPatientInvolved: {
                custom: {
                    isValid: (value, data) => {
                        if (data.wasProductPlaced === 'NO') {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            wasOpenedOrDropped: {
                custom: {
                    isValid: (value, data) => {
                        if (
                            data.productType === 'IMPLANT'
                            && (['us', 'ca', 'hk'].includes(countryCode))
                            && (['Nobel'].includes(orgIdentifier))
                        ) {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            wasAnotherImplantPlaced: {
                custom: {
                    isValid: (value, data) => {
                        if (
                            data.productType === 'IMPLANT' &&
                            data.wasProductPlaced === 'YES'
                        ) {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('this_field_is_required'),
                },
            },
            batchOrLotNumber: {
                custom: {
                    isValid: (value, data) => {
                        // For Nobel: only validate if "YES" was selected
                        // For IMDI: always validate (no question shown)
                        if (orgIdentifier === "IMDI" || data.isBatchOrLotNumberKnown === 'YES') {
                            // If the field is empty
                            if (!value) {
                                return false
                            }
                        }
                        return true
                    },
                    message: t('pt_valid_batch_or_lot_number_is_required'),
                },
            },
        },
    })

    const anotherImplantInitialValue: YesNoOptions = complaint.generalComplaint
        .wasAnotherImplantPlaced as YesNoOptions

    const productTypeInitialValue: ProductTypeOptions = complaint
        .generalComplaint.productType as ProductTypeOptions

    const batchNumInitialValue: string = complaint.generalComplaint
        .batchOrLotNumber as string

    const productTypeMap = {
        IMPLANT: t('pt_option_implant'),
        ABUTMENT: t('pt_option_abutment'),
        SCREW: t('pt_option_screw'),
        CLINICAL_INSTRUMENT: t('pt_option_clinical_instrument'),
        LAB_INSTRUMENT_LAB_SCREW: t('pt_option_lab_instrument_lab_screw'),
    }

    // A third-party product should never reach this screen from the normal primary-complaint
    // flow (ProductSearch already blocks it there). It can only reach here when a secondary
    // complaint is created from a secondary device that happens to be a third-party product.
    // In that case we must show the third-party error and prevent the user from proceeding.
    // Use a loose check (matches ProductSearch.tsx) so we also catch cases where the flag is
    // missing/undefined after being carried over from the secondary device state.
    const isThirdParty = Boolean(
        data.selectedProduct
        && !data.isProductUnknown
        && !data.selectedProduct.isFirstPartyManufactured
    )

    const handlePatientInvolvedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const wasPatientInvolved = event.target.value as YesNoOptions
        
        // If changing to 'NO', reset treatment dates
        if (wasPatientInvolved === 'NO' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates())
        }
        
        // Call the original handler
        handleChangeEvent('wasPatientInvolved')(event)
    }

    const handleProductPlacedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const wasProductPlaced = event.target.value as YesNoOptions
        
        // If changing to 'NO', reset treatment dates
        if (wasProductPlaced === 'NO' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates())
        }
        
        // Call the original handler
        handleChangeEvent('wasProductPlaced')(event)
    }

    const handleAnotherImplantPlacedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const wasAnotherImplantPlaced = event.target.value as YesNoOptions
        
        // If changed from YES to NO, need to reset relevant treatment dates
        if (anotherImplantInitialValue === 'YES' && 
            wasAnotherImplantPlaced === 'NO' && 
            !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates())
        }
        
        // If changing to YES, set default treatment date values.
        // NOTE: the "was the product removed" radio actually asks "Is the product STILL in
        // the patient's mouth?" and its options are inverted (value 'YES' == "No, not in
        // mouth"). Another implant on the same site means the product is no longer in the
        // mouth, so we force 'YES' here, which also makes the removal date mandatory.
        if (wasAnotherImplantPlaced === 'YES' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(setTreatmentDates({
                wasProductRemoved: "YES",
                dateProductWasRemoved: null,
                wasThereASecondSurgery: "NO",
                dateOfSecondSurgery: null,
            }))
        }
        
        // Call the original handler
        handleChangeEvent('wasAnotherImplantPlaced')(event)
    }

    const handleProductTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedProductType = event.target.value as ProductTypeOptions;
      
        // Check if we're changing from a type that requires treatment dates to one that doesn't
        const requiresTreatmentDates = (type: ProductTypeOptions) => 
        type === 'IMPLANT' || type === 'ABUTMENT'

        // If changing product type in a way that affects date requirements
        if (requiresTreatmentDates(productTypeInitialValue) && 
            !requiresTreatmentDates(selectedProductType) && 
            !complaint.metadata.complaintWithPatientAndTreatmentData) {
                dispatch(resetTreatmentDates());
        }
        
        // Update the form state with both productType and selectedProduct.productId.
        const productNumberMap: Record<ProductTypeOptions, string> = {
          '': 'XUN001', // Default value for empty string case
          IMPLANT: 'XUN001',
          ABUTMENT: 'XUN002',
          SCREW: 'XUN003',
          CLINICAL_INSTRUMENT: 'XUN004',
          LAB_INSTRUMENT_LAB_SCREW: 'XUN006',
        };
      
        setFormData((prevData) => ({
          ...prevData,
          productType: selectedProductType,
          selectedProduct: {
            ...prevData.selectedProduct!,
            productId: productNumberMap[selectedProductType],
          },
        }));
    };

    const handleBatchNumberOnBlur = () => {
        // Don't validate if batch number is empty
        if (!data.batchOrLotNumber || data.batchOrLotNumber.trim() === '') {
            setIsBatchNumValid(false);
            setShowBatchNumCheck(false);
            return;
        }

        const cancelTokenSource = axios.CancelToken.source()
        const batchNum = data.batchOrLotNumber

        const validateBatchNum = async (batchNum: string) => {
            try {
                setIsValidatingBatchNum(true)

                let response = await http.get<MaterialResponse>(
                    `/api/Product/Validate?material=${data.selectedProduct?.productId}&batch=${batchNum}&customerNumber=${customerNumber}&countryCode=${countryCode}`,
                    {cancelToken: cancelTokenSource.token})

                if (
                    response &&
                    response.data &&
                    response.data.materialList &&
                    response.data.materialList.length > 0
                ) {
                    if (
                        response.data.materialList[0].batchReturnCode === '300'
                    ) {
                        setIsBatchNumValid(true)
                        setShowBatchNumCheck(true)
                    } else {
                        setIsBatchNumValid(false)
                        setShowBatchNumCheck(false)
                    }
                }

                setWasValidated(true) // Mark that validation has occurred
                setIsValidatingBatchNum(false)
            } catch (e) {
                // Handle error by marking batch number as invalid
                setIsBatchNumValid(false)
                setShowBatchNumCheck(false)
                setIsValidatingBatchNum(false)
                setWasValidated(true) // Mark that validation has occurred
                console.error('Error validating batch number:', e)
            }
        }

        const resetBatchNumState = () => {
            setIsBatchNumValid(false) // Default to invalid until validated
            setShowBatchNumCheck(false)
            setIsValidatingBatchNum(false)
            setWasValidated(false) // Reset validation status
        }

        if (batchNum) {
            // Always validate batch number, even if it matches the initial value
            validateBatchNum(batchNum)
        } else {
            resetBatchNumState()
        }

        return () => {
            cancelTokenSource.cancel()
        }
    }

    // Validate batch number when component mounts (for returning to the screen with a saved batch number)
    React.useEffect(() => {
        // If there's a saved batch number from previous visit, validate it
        if (data.batchOrLotNumber && data.batchOrLotNumber.trim() !== '') {
            const validateSavedBatchNum = async () => {
                try {
                    setIsValidatingBatchNum(true);
                    let response = await http.get<MaterialResponse>(
                        `/api/Product/Validate?material=${data.selectedProduct?.productId}&batch=${data.batchOrLotNumber}&customerNumber=${customerNumber}&countryCode=${countryCode}`
                    );
                    
                    if (response?.data?.materialList?.length > 0) {
                        setIsBatchNumValid(response.data.materialList[0].batchReturnCode === '300');
                        setShowBatchNumCheck(response.data.materialList[0].batchReturnCode === '300');
                    } else {
                        setIsBatchNumValid(false);
                        setShowBatchNumCheck(false);
                    }
                    setWasValidated(true); // Mark as validated
                    setIsValidatingBatchNum(false);
                } catch (e) {
                    setIsBatchNumValid(false);
                    setShowBatchNumCheck(false);
                    setWasValidated(true); // Mark as validated even in error case
                    setIsValidatingBatchNum(false);
                }
            };
            
            validateSavedBatchNum();
        }
    }, []);

    // Set isBatchOrLotNumberKnown to 'YES' for IMDI organizations when component mounts
    React.useEffect(() => {
        if (orgIdentifier === "IMDI") {
            setFormData(prevData => ({
                ...prevData,
                isBatchOrLotNumberKnown: 'YES'
            }));
        }
    }, [orgIdentifier, setFormData]);
    
    // Handle batch number changes to immediately reset validation state
    const handleBatchNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Reset validation state as soon as user starts typing
        setIsBatchNumValid(false);
        setShowBatchNumCheck(false);
        setIsValidatingBatchNum(false); // Also reset validation-in-progress flag
        setWasValidated(false); // Reset validation status when typing
        
        // Call the original form change handler
        handleChangeEvent('batchOrLotNumber')(e);
    };

    const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        // Block submission for third-party secondary-device products carried into a secondary complaint
        if (isThirdParty) return

        // Activate field level validation tracking
        enableOnChangeValidation(true)

        // Validate the form fields
        let isFormValid: boolean = validateFields(data)
        
        // Additional validation for batch number
        if (isFormValid) {
            if (orgIdentifier === "Nobel" && data.isBatchOrLotNumberKnown === 'YES') {
                // For Nobel: Only validate batch number if "YES" was selected
                isFormValid = isBatchNumValid && !!data.batchOrLotNumber
            } else if (orgIdentifier === "IMDI") {
                // For IMDI: Always validate batch number (no question shown)
                isFormValid = isBatchNumValid && !!data.batchOrLotNumber
            }
        }

        // Stop processing if the form has invalid fields
        if (
            !isFormValid ||
            (data.wasOpenedOrDropped === 'YES'
                && orgIdentifier === "Nobel"
                && data.productType === 'IMPLANT')
        ) {
            postNotification('error', data.wasOpenedOrDropped === 'YES' && data.productType === 'IMPLANT'
                ? t('error_mess_opened_or_dropped')
                : t('error_mess'))
            return
        }

        // Form is valid, proceed with processing

        // Close the alert message in case it is open
        clearNotification()

        console.log('Dispatching generalComplaint:', {
            ...data,
            productId: data.selectedProduct?.productId || '',
            // ...other fields
        });

        // Package up the form data and update app state
        /*const updatedSelectedProduct = {
            // Make sure all fields are explicitly set
            ...data.selectedProduct,
            productId: data.selectedProduct?.productId ?? '', // Ensure productId is a string
            salesOrg: data.selectedProduct?.salesOrg ?? '', 
            action: data.selectedProduct?.action ?? '', 
            productName: data.selectedProduct?.productName ?? '', 
            validFrom: data.selectedProduct?.validFrom ?? '', 
            shortDescription: data.selectedProduct?.shortDescription ?? '', 
            materialPrice: data.selectedProduct?.materialPrice ?? '', // Ensure materialPrice is a string
            materialPriceCurrency: data.selectedProduct?.materialPriceCurrency ?? '', // Ensure materialPriceCurrency is a string
            categories: data.selectedProduct?.categories ?? null, // Ensure categories is either string[] or null
            isDiscontinuedOrUnknown: data.selectedProduct?.isDiscontinuedOrUnknown ?? false, // Ensure isDiscontinuedOrUnknown is a boolean
            isFirstPartyManufactured: data.selectedProduct?.isFirstPartyManufactured ?? false, // Ensure isFirstPartyManufactured is a boolean
        }*/

        const generalComplaint: GeneralComplaintState = {
            ...data,
            /*selectedProduct: updatedSelectedProduct, // explicitly include the updated nested object*/
            productId: data.selectedProduct?.productId as ProductIdOptions,
            batchOrLotNumber:
                orgIdentifier === "IMDI" || data.isBatchOrLotNumberKnown === 'YES'
                    ? data.batchOrLotNumber
                    : '',
            wasPatientInvolved:
                data.wasProductPlaced === 'NO'
                    ? data.wasPatientInvolved
                    : 'YES',
            wasOpenedOrDropped:
                data.productType === 'IMPLANT' ? data.wasOpenedOrDropped : '',
            wasAnotherImplantPlaced:
                data.productType === 'IMPLANT' &&
                data.wasProductPlaced === 'YES'
                    ? data.wasAnotherImplantPlaced
                    : '',
        }

        // Clear out future fields in the case of changed selections, i.e., the
        // user made selections on this screen, continued to other screens, and
        // then returned to this screen and changed their initial selections

        // Clear out Treatment Date fields that are relevant to another implant placed
        if (
            anotherImplantInitialValue === 'YES' &&
            data.wasAnotherImplantPlaced === 'NO' &&
            !complaint.metadata.complaintWithPatientAndTreatmentData
        ) {
            dispatch(resetTreatmentDates())
        }

        // Clear out Treatment Dates if the user returns to the product type screen
        // and changes the product type
        if (productTypeInitialValue !== data.productType && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates())
        }

        // Clear out complaint type and clinical conditions data if the user returns to
        // the product type screen and changes the product type
        if (productTypeInitialValue !== data.productType) {
            dispatch(resetComplaintType())
            dispatch(resetClinicalCondition())
        }

        if (data.wasAnotherImplantPlaced === 'YES' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(setTreatmentDates({
                wasProductRemoved: "YES",
                dateProductWasRemoved: null,
                wasThereASecondSurgery: "NO",
                dateOfSecondSurgery: null,
            }))
        }
        
        // Ensure all date fields are reset when changing to selections that don't require them
        if ((!data.wasPatientInvolved || data.wasPatientInvolved === 'NO') && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates());
        }

        if (data.wasProductPlaced === 'NO' && !complaint.metadata.complaintWithPatientAndTreatmentData) {
            dispatch(resetTreatmentDates());
        }

        // return
        dispatch(setGeneralComplaint(generalComplaint))

        // Proceed to next screen
        navigateForward()
    }

    return (
        <Grid container>
            <Grid item xs={12}>
                <Typography variant='h5' sx={{mb: 2}}>
                    {t('pt_selected_product')}
                </Typography>
            </Grid>

            <Stack className={classes.table}
                   sx={{width: '100%'}}
                   direction={{xs: 'column', sm: 'column', md: 'column', lg: 'column', xl: 'row'}}
                   gap={1}
                   justifyContent='space-between'>
                <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('material_number')}
                    </Typography>
                    <Typography variant='body1'>
                        {data.selectedProduct?.productId}
                    </Typography>
                </Box>
                <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('description')}
                    </Typography>
                    <Typography variant='body1'>
                        {selectedProduct?.shortDescription}
                    </Typography>
                </Box>
                {selectedProduct?.productType && <Box>
                    <Typography
                        variant='subtitle1'
                        className={classes.tableHeading}
                    >
                        {t('product_type')}
                    </Typography>
                    <Typography variant='body1'>
                        {productTypeMap[selectedProduct.productType]}
                    </Typography>
                </Box>}
            </Stack>

            {isThirdParty && (
                <Grid item xs={12} sx={{mt: 2}}>
                    <Alert color='error'>
                        <ThirdPartyError/>
                    </Alert>
                </Grid>
            )}

            <Grid item xs={12} sx={{mt: 2}}>
                <form onSubmit={handleFormSubmit}>
                    {!isThirdParty && <>
                    {orgIdentifier === "Nobel" ? (
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_is_batch_or_lot_number_known')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.isBatchOrLotNumberKnown}
                                    onChange={handleChangeEvent(
                                        'isBatchOrLotNumberKnown'
                                    )}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                {countryCode === 'jp' &&
                                        language === 'ja' && (
                                        <Alert  severity="info"
                                                sx={{
                                                    ml: 35, mb: 3, mt: -8, width: 'fit-content',
                                                    minHeight: 20, py:0,
                                                }}
                                            >
                                                {t('pt_is_batch_or_lot_number_known_jp')}
                                            </Alert>
                                        )}
                                    {countryCode === 'jp' &&
                                        language === 'ja' &&
                                        (data.isBatchOrLotNumberKnown === 'NO') && (
                                            <Alert severity="error" icon={false} sx={{
                                                backgroundColor: 'transparent',
                                                color: '#D32F2F',
                                                fontWeight: 'bold',
                                                paddingLeft: 0,

                                            }}>
                                                {t('pt_is_batch_or_lot_number_known_jp')}
                                            </Alert>
                                        )}
                                <FormHelperText
                                    error={!!errors.isBatchOrLotNumberKnown}
                                >
                                    {errors.isBatchOrLotNumberKnown}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    ) : null}
                    {(orgIdentifier === "IMDI" || data.isBatchOrLotNumberKnown === 'YES') && (
                        <Grid item xs={12} sx={{mt: 0}}>
                            <FormControl sx={{mb: 2}}>
                                <FormLabel sx={{mb: 1}}>
                                    {t('pt_enter_batch_or_lot_number')}:
                                </FormLabel>
                                <TextField
                                    id='batch-lot-number'
                                    error={
                                        !!errors.batchOrLotNumber || (wasValidated && !isBatchNumValid && !!data.batchOrLotNumber && data.batchOrLotNumber.trim() !== '')
                                    }
                                    size='small'
                                    // helperText={errors.batchOrLotNumber}
                                    helperText={
                                        errors.batchOrLotNumber 
                                            ? t('this_field_is_required')
                                            : (wasValidated && !isBatchNumValid && !!data.batchOrLotNumber && data.batchOrLotNumber.trim() !== '')
                                                ? t('pt_valid_batch_or_lot_number_is_required')
                                                : null
                                    }
                                    value={data.batchOrLotNumber}
                                    onChange={handleBatchNumberChange}
                                    inputProps={{maxLength: 10}}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                {isValidatingBatchNum && (
                                                    <CircularProgress size='1rem'/>
                                                )}
                                                {!isValidatingBatchNum && 
                                                    isBatchNumValid &&
                                                    data.batchOrLotNumber && 
                                                    data.batchOrLotNumber.length > 0 && (
                                                        <CheckIcon
                                                            sx={{
                                                                color: '#666)',
                                                            }}
                                                        />
                                                    )}
                                            </InputAdornment>
                                        ),
                                    }}
                                    onBlur={handleBatchNumberOnBlur}
                                />
                            </FormControl>
                        </Grid>
                    )}

                    {!complaint.metadata.complaintWithPatientAndTreatmentData &&
                        <Grid item xs={12} sx={{mt: 1}}>
                            <Stack spacing={2} >  
                            <FormControl>
                                <FormLabel>
                                    {t('pt_was_the_product_placed')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasProductPlaced}
                                    onChange={handleProductPlacedChange}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText error={!!errors.wasProductPlaced}>
                                    {errors.wasProductPlaced}
                                </FormHelperText>
                            </FormControl>
                            </Stack>
                        </Grid>
                    }

                    {data.wasProductPlaced === 'NO' && (
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_was_patient_involved')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasPatientInvolved}
                                    onChange={handlePatientInvolvedChange}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText
                                    error={!!errors.wasPatientInvolved}
                                >
                                    {errors.wasPatientInvolved}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    )}
                    
                    {!complaint.generalComplaint.selectedProduct?.productType && <Grid item xs={12} sx={{mt: 1}}>
                        <FormControl>
                            <FormLabel id='product-type-radio-buttons'>
                                {t('pt_select_product_type')}:
                            </FormLabel>
                            <RadioGroup
                                name='radio-buttons-group'
                                value={data.productType}
                                onChange={handleProductTypeChange}
                            >
                                <FormControlLabel
                                    value='IMPLANT'
                                    control={<Radio/>}
                                    label={t('pt_option_implant')}
                                />
                                <FormControlLabel
                                    value='ABUTMENT'
                                    control={<Radio/>}
                                    label={t('pt_option_abutment')}
                                />
                                <FormControlLabel
                                    value='SCREW'
                                    control={<Radio/>}
                                    label={t('pt_option_screw')}
                                />
                                <FormControlLabel
                                    value='CLINICAL_INSTRUMENT'
                                    control={<Radio/>}
                                    label={t('pt_option_clinical_instrument')}
                                />
                                <FormControlLabel
                                    value='LAB_INSTRUMENT_LAB_SCREW'
                                    control={<Radio/>}
                                    label={t('pt_option_lab_instrument_lab_screw')}
                                />
                            </RadioGroup>
                            <FormHelperText error={!!errors.productType}>
                                {errors.productType}
                            </FormHelperText>
                        </FormControl>
                    </Grid>}
                    {data.productType === 'IMPLANT' && (['us', 'ca', 'hk'].includes(countryCode)) && (
                        <Grid item xs={12} sx={{mt: 1}}>
                            <FormControl>
                                <FormLabel>
                                    {t('pt_implant_opened_or_dropped')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={data.wasOpenedOrDropped}
                                    onChange={handleChangeEvent(
                                        'wasOpenedOrDropped'
                                    )}
                                >
                                    <FormControlLabel
                                        value='YES'
                                        control={<Radio/>}
                                        label={t('option_yes') as string}
                                    />
                                    <FormControlLabel
                                        value='NO'
                                        control={<Radio/>}
                                        label={t('option_no') as string}
                                    />
                                </RadioGroup>
                                <FormHelperText
                                    error={!!errors.wasOpenedOrDropped}
                                >
                                    {errors.wasOpenedOrDropped}
                                </FormHelperText>
                            </FormControl>
                            {
                                orgIdentifier === "Nobel"
                                    ? <IRSAlert wasOpenedOrDropped={data.wasOpenedOrDropped as YesNoOptions}/>
                                    : null
                            }

                        </Grid>
                    )}


                    {data.productType === 'IMPLANT' && (!complaint.metadata.complaintWithPatientAndTreatmentData ||
                        complaint.generalComplaint.wasAnotherImplantPlaced === '') && data.wasProductPlaced === 'YES' && (
                            <Grid item xs={12} sx={{mt: 1}}>
                                <FormControl>
                                    <FormLabel>
                                        {t('pt_another_implant_placed')}
                                    </FormLabel>
                                    <RadioGroup
                                        row
                                        value={data.wasAnotherImplantPlaced}
                                        onChange={handleAnotherImplantPlacedChange}
                                    >
                                        <FormControlLabel
                                            value='YES'
                                            control={<Radio/>}
                                            label={t('option_yes') as string}
                                        />
                                        <FormControlLabel
                                            value='NO'
                                            control={<Radio/>}
                                            label={t('option_no') as string}
                                        />
                                    </RadioGroup>
                                    <FormHelperText
                                        error={!!errors.wasAnotherImplantPlaced}
                                    >
                                        {errors.wasAnotherImplantPlaced}
                                    </FormHelperText>
                                </FormControl>
                            </Grid>
                        )}
                    </>}

                    <Grid
                        item
                        container
                        direction='row'
                        justifyContent='flex-end'
                        className={classes.buttons}
                        sx={{pt: 4}}
                    >
                        <ControlContainer>
                            <Button
                                disabled={
                                    isThirdParty ||
                                    (isValidatingBatchNum &&
                                    data.isBatchOrLotNumberKnown === 'YES')
                                }
                                variant='contained'
                                color='primary'
                                type='submit'
                            >
                                {t('continue')}
                            </Button>

                            <PreviousButton before={() => dispatch(setGeneralComplaint(data))}/>

                            <CancelButton/>
                        </ControlContainer>
                    </Grid>
                </form>
            </Grid>
        </Grid>
    )
}
export default ProductType
